import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createTestbuy,
  getTestbuy,
  replaceTestbuyArticle,
  updateTestbuyArticleCriterion,
  type CreateTestbuyParams,
  type ReplaceTestbuyArticleParams,
  type Testbuy,
} from "@coworker/apprestructured/src/testbuy/services/testbuy.service";
import {
  getTestbuyQueryKey,
  invalidateTestbuyQueries,
  patchTestbuyArticleCriterionCache,
} from "@coworker/apprestructured/src/testbuy/services/queryKeyHelper";
import { Criterion } from "@coworker/types/lib/testbuy";

function getTestbuyQueryConfig(testbuyId: string | null) {
  return {
    queryKey: getTestbuyQueryKey(testbuyId),
    queryFn: () => getTestbuy(testbuyId!),
    enabled: testbuyId !== null,
  };
}

/**
 * Fetches a testbuy by id, including its articles and their criteria.
 */
export function useGetTestbuy(testbuyId: string | null) {
  return useQuery(getTestbuyQueryConfig(testbuyId));
}

/**
 * Creates a testbuy linked to a Task Management V2 task. Does not need to invalidate the
 * testbuy query itself (the id isn't known yet), but the caller should invalidate the task
 * queries once it has the returned `testBuyId`, since the task is updated with a link to it.
 */
export function useCreateTestbuy() {
  return useMutation({
    mutationFn: (params: CreateTestbuyParams) => createTestbuy(params),
  });
}

/**
 * Replaces an article in a testbuy, invalidating the testbuy query on success so the
 * replaced article (and its fresh criteria) are refetched.
 */
/** @public */
export function useReplaceTestbuyArticle(testbuyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      articleId,
      params,
    }: {
      articleId: string;
      params: ReplaceTestbuyArticleParams;
    }) => replaceTestbuyArticle(testbuyId, articleId, params),
    onSuccess: () => invalidateTestbuyQueries(queryClient, testbuyId),
  });
}

/**
 * Updates a single criterion of a testbuy article. Instead of invalidating (and refetching)
 * the whole testbuy, patches the affected criterion directly into the cache. The cache is
 * patched optimistically (in `onMutate`) so the radio button reflects the new state
 * instantly instead of waiting for the request to resolve; if the request fails, the
 * previous cache snapshot is restored.
 */
export function useUpdateTestbuyArticleCriterion(testbuyId: string) {
  const queryClient = useQueryClient();
  const queryKey = getTestbuyQueryKey(testbuyId);

  return useMutation({
    mutationFn: ({
      articleId,
      criterion,
    }: {
      articleId: string;
      criterion: Criterion;
    }) => updateTestbuyArticleCriterion(testbuyId, articleId, criterion),
    onMutate: async ({ articleId, criterion }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousTestbuy = queryClient.getQueryData<Testbuy>(queryKey);

      patchTestbuyArticleCriterionCache(
        queryClient,
        testbuyId,
        articleId,
        criterion
      );

      return { previousTestbuy };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousTestbuy) {
        queryClient.setQueryData(queryKey, context.previousTestbuy);
      }
    },
  });
}
